import React from "react";
import { useTheme } from "styled-components";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { tokens } from "../../activity-logs/styles";
import { formatCompact, formatCurrency } from "../dashboardData";

export const ChartTooltip = ({ active, payload, label, showTotal = true }) => {
  const theme = useTheme();
  if (!active || !payload?.length) return null;
  const total = payload.reduce((sum, p) => sum + (p.value || 0), 0);
  return (
    <div style={{
      background: tokens.solid(theme),
      border: `1px solid ${tokens.separator(theme)}`,
      borderRadius: 10,
      padding: "10px 12px",
      boxShadow: tokens.dk(theme) ? "0 8px 24px rgba(0,0,0,0.45)" : "0 8px 24px rgba(15,23,42,0.08)",
      fontSize: 12,
      fontVariantNumeric: "tabular-nums",
      color: tokens.label(theme),
      minWidth: 170,
    }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>{label}</div>
      {[...payload].reverse().map((p) => (
        <div key={p.dataKey} style={{ display: "flex", justifyContent: "space-between", gap: 14, marginTop: 2 }}>
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: p.color }} />
            {p.name}
          </span>
          <strong>{formatCurrency(p.value)}</strong>
        </div>
      ))}
      {showTotal && payload.length > 1 && (
        <div style={{ display: "flex", justifyContent: "space-between", gap: 14, marginTop: 6, paddingTop: 6, borderTop: `1px solid ${tokens.separator(theme)}` }}>
          <span>Total</span><strong>{formatCurrency(total)}</strong>
        </div>
      )}
    </div>
  );
};

const useAxis = () => {
  const theme = useTheme();
  return {
    grid: <CartesianGrid vertical={false} stroke={tokens.separator(theme)} />,
    axis: { tick: { fontSize: 10, fill: tokens.label2(theme) }, axisLine: false, tickLine: false },
    margin: { top: 10, right: 4, left: 0, bottom: 0 },
  };
};

// Evolução contínua de um único valor; com menos de 3 pontos, área não faz sentido
export function TrendChart({ data, dataKey, name, color, height = 220, animate = true }) {
  const theme = useTheme();
  const { grid, axis, margin } = useAxis();
  const x = <XAxis dataKey="label" minTickGap={18} {...axis} />;
  const y = <YAxis width={70} tickFormatter={formatCompact} {...axis} />;

  if (data.length < 3) return <DocumentBars data={data} dataKey={dataKey} name={name} color={color} height={height} />;

  const gradient = `trend-${dataKey}`;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={margin}>
        <defs>
          <linearGradient id={gradient} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.22} />
            <stop offset="100%" stopColor={color} stopOpacity={0.02} />
          </linearGradient>
        </defs>
        {grid}{x}{y}
        <Tooltip content={<ChartTooltip />} cursor={{ stroke: tokens.label3(theme), strokeDasharray: "3 3" }} />
        <Area type="monotone" dataKey={dataKey} name={name} stroke={color} strokeWidth={1.5} fill={`url(#${gradient})`} isAnimationActive={animate} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

// Valor por período em barras finas: documentos são eventos, não uma curva
export function DocumentBars({ data, dataKey, name, color, height = 220 }) {
  const theme = useTheme();
  const { grid, axis, margin } = useAxis();
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={margin}>
        {grid}
        <XAxis dataKey="label" minTickGap={18} {...axis} />
        <YAxis width={70} tickFormatter={formatCompact} {...axis} />
        <Tooltip content={<ChartTooltip />} cursor={{ fill: tokens.fill(theme) }} />
        <Bar dataKey={dataKey} name={name} fill={color} fillOpacity={0.8} maxBarSize={14} radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}
